import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Shuffle, ShieldCheck, X } from "lucide-react";
import { HandleAvatar } from "@/components/HandleAvatar";

export function HandlePicker({ current, onSave, onClose }) {
  const [handle, setHandle] = useState(current || "");
  const [busy, setBusy] = useState(false);

  const suggest = async () => {
    const r = await api.get("/community/handle/suggest");
    setHandle(r.data.handle);
  };
  useEffect(() => { if (!current) suggest(); }, [current]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await onSave(handle.trim());
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/50 backdrop-blur-sm p-4" data-testid="handle-picker">
      <form onSubmit={save} className="warm-card p-6 w-full max-w-md space-y-4 relative">
        <button type="button" onClick={onClose} data-testid="handle-close" className="absolute top-4 right-4 w-8 h-8 rounded-full bg-stone-100 hover:bg-stone-200 flex items-center justify-center"><X className="w-4 h-4" /></button>
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-emerald-700" />
          <h3 className="font-display text-lg font-bold text-stone-900">{current ? "Change your handle" : "Choose a community handle"}</h3>
        </div>
        <p className="text-sm text-stone-600">
          This is the only thing members will ever see. To protect your privacy it can't include any part of your name or email — pick something that says nothing about who you are.
        </p>
        <div className="flex items-center gap-3 rounded-2xl bg-stone-50 px-4 py-3" data-testid="handle-preview">
          <HandleAvatar handle={handle} size="lg" />
          <div>
            <div className="font-display font-bold text-stone-900">{handle.trim() || "Your handle"}</div>
            <div className="text-xs text-stone-500">Your icon and colour come from the words in your handle.</div>
          </div>
        </div>
        <div className="flex gap-2">
          <input
            data-testid="handle-input"
            value={handle}
            onChange={(e) => setHandle(e.target.value)}
            maxLength={24}
            placeholder="e.g. Quiet Otter"
            className="flex-1 px-4 py-2.5 rounded-xl border border-stone-300 focus:border-amber-600 focus:ring-2 focus:ring-amber-200 outline-none font-semibold"
          />
          <button type="button" data-testid="handle-suggest" onClick={suggest} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-stone-100 hover:bg-stone-200 text-sm font-semibold text-stone-700">
            <Shuffle className="w-4 h-4" /> Suggest
          </button>
        </div>
        <p className="text-xs text-stone-400">3–24 characters · letters, numbers and spaces · must be unique</p>
        <button data-testid="handle-save" type="submit" disabled={busy || handle.trim().length < 3} className="amber-btn w-full text-sm disabled:opacity-50">
          {current ? "Save handle" : "Save & turn on sharing"}
        </button>
      </form>
    </div>
  );
}
